import { apiClient } from './apiClient';
import { db } from '../lib/database';
import { API_ENDPOINTS } from '../config/api';
import { questionResponseService } from './questionResponseService';
import { fileUploadService } from './fileUploadService';

const saveFormStructureFromSubmission = submission => {
  if (!submission.form) return Promise.resolve();
  return db.forms.put({ ...submission.form, title: submission.form.name });
};

export const submissionService = {
  submitForm: submissionPayload => {
    return apiClient.post(API_ENDPOINTS.submissions.submit, submissionPayload, { suppressToast: true });
  },

  fetchSubmissions: (page = 1, limit = 1000, forceOffline = false) => {
    if (!forceOffline && navigator.onLine) {
      const endpoint = `${API_ENDPOINTS.submissions.list}?page=${page}&limit=${limit}`;
      return apiClient
        .get(endpoint, { suppressToast: true })
        .then(response => {
          const responseData = response.data.data;
          const submissionsFromApi = responseData?.data || [];
          const pagination = responseData?.pagination || {};
          const hasData = submissionsFromApi.length > 0;

          if (page === 1) {
            db.settings.put({ key: 'submissions_restricted', value: !hasData });
            if (!hasData) {
              return db.submissions
                .where('syncStatus')
                .equals('synced')
                .delete()
                .then(() => ({ data: { data: [], hasMore: false } }));
            }
          }

          const formSavePromises = submissionsFromApi.map(saveFormStructureFromSubmission);

          return Promise.all(formSavePromises)
            .then(() => {
              const processedSubmissions = submissionsFromApi.map(submission => {
                const answersData = (submission.answers || []).reduce((acc, curr) => {
                  acc[curr.form_field_id] = curr.value_json || curr.value_json || curr.value;
                  return acc;
                }, {});

                const submissionToStore = {
                  ...submission,
                  answers: answersData,
                  syncStatus: 'synced',
                  formName: submission.form?.name || submission.formName,
                  formStatus: submission.form?.status || submission.formStatus
                };
                delete submissionToStore.form;
                return submissionToStore;
              });

              return db.transaction('rw', db.submissions, async () => {
                await db.submissions.bulkPut(processedSubmissions);
                if (page === 1) {
                  const apiIds = new Set(processedSubmissions.map(s => s.id));
                  const allSynced = await db.submissions.where('syncStatus').equals('synced').toArray();
                  const idsToDelete = allSynced.filter(s => !apiIds.has(s.id)).map(s => s.id);

                  if (idsToDelete.length > 0) {
                    await db.submissions.bulkDelete(idsToDelete);
                  }
                }

                return processedSubmissions;
              });
            })
            .then(finalSubmissions => ({
              data: {
                data: finalSubmissions,
                hasMore: !!pagination.next_page
              }
            }));
        })
        .catch(error => {
          const status = error.response?.status;
          if (status === 401 || status === 403) {
            throw error;
          }
          return db.submissions.toArray().then(localSubmissions => ({
            data: { data: localSubmissions, hasMore: false }
          }));
        });
    } else {
      return db.submissions.toArray().then(localSubmissions => ({
        data: { data: localSubmissions, hasMore: false }
      }));
    }
  },

  getSubmissionById: (submissionId, forceOffline = false) => {
    const fetchFromDb = () => {
      return db.submissions.get(submissionId).then(submission => {
        if (!submission) throw new Error(`Submission ${submissionId} not found locally.`);
        return { data: { data: submission } };
      });
    };

    if (forceOffline || !navigator.onLine || submissionId.startsWith('temp_')) {
      return fetchFromDb();
    }

    return apiClient.get(API_ENDPOINTS.submissions.details(submissionId), { suppressToast: true }).catch(error => {
      const status = error.response?.status;
      // Do not fall back to local DB if it's a permission/Auth error
      if (status === 401 || status === 403) {
        throw error;
      }
      return fetchFromDb();
    });
  },

  deleteSubmission: submissionId => {
    if (submissionId.startsWith('temp_')) {
      return db.outbox
        .where({ tempId: submissionId })
        .delete()
        .then(() => questionResponseService.deleteSubmissionResponses(submissionId))
        .then(() => fileUploadService.deleteSubmissionFiles(submissionId))
        .then(() => db.submissions.delete(submissionId))
        .then(() => ({ success: true, message: 'Local submission deleted.' }));
    }

    if (!navigator.onLine) {
      return db.submissions
        .update(submissionId, { syncStatus: 'deleted' })
        .then(() =>
          import('./syncService').then(({ syncService }) =>
            syncService.addToOutbox({
              endpoint: API_ENDPOINTS.submissions.delete(submissionId),
              method: 'DELETE',
              payload: { id: submissionId }
            })
          )
        )
        .then(() => ({ success: true, message: 'Submission marked for deletion.' }));
    }

    return apiClient
      .delete(API_ENDPOINTS.submissions.delete(submissionId))
      .then(() => db.submissions.delete(submissionId))
      .then(() => questionResponseService.deleteSubmissionResponses(submissionId))
      .then(() => fileUploadService.deleteSubmissionFiles(submissionId))
      .then(() => ({ success: true, message: 'Submission deleted.' }));
  },

  updateSubmission: (submissionId, submissionPayload) => {
    if (submissionId.startsWith('temp_')) {
      // For local temp submissions, just update the DB and the outbox entry
      return db.submissions
        .update(submissionId, {
          answers: submissionPayload.answers_flat || submissionPayload.answers,
          updated_at: new Date().toISOString()
        })
        .then(() => db.outbox.where({ tempId: submissionId }).modify({ payload: submissionPayload }))
        .then(() => ({ success: true, message: 'Local submission updated.' }));
    }

    if (!navigator.onLine) {
      // For synced submissions edited offline
      return db.submissions
        .update(submissionId, {
          answers: submissionPayload.answers_flat || submissionPayload.answers,
          syncStatus: 'pending',
          updated_at: new Date().toISOString()
        })
        .then(() =>
          import('./syncService').then(({ syncService }) =>
            syncService.addToOutbox({
              endpoint: API_ENDPOINTS.submissions.update(submissionId),
              method: 'PATCH',
              payload: submissionPayload,
              submissionId: submissionId
            })
          )
        )
        .then(() => ({ success: true, message: 'Submission updated offline.' }));
    }

    // Online update
    return apiClient.patch(API_ENDPOINTS.submissions.update(submissionId), submissionPayload).then(response => {
      // Update local cache with fresh data from server if possible, or just merge
      return db.submissions
        .update(submissionId, {
          ...response.data.data,
          syncStatus: 'synced',
          updated_at: new Date().toISOString()
        })
        .then(() => response);
    });
  }
};
